import { Form, Head } from "@inertiajs/react";
import { useTranslation } from "react-i18next";

import { AppearanceForm } from "@/components/settings/appearance-form";
import { ProfileForm } from "@/components/settings/profile-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import SettingsLayout from "@/layouts/settings_layout";
import { Button } from "@/components/ui/button";
import { userSessionPath } from "@/routes";
import type { Session, User } from "@/types";

export default function ProfileSettings({
  user,
  sessions,
}: {
  user: User;
  sessions: Array<Session & { current: boolean }>;
}) {
  const { t } = useTranslation();
  return (
    <SettingsLayout>
      <Head title={t("common.profile")} />
      <div className="space-y-6">
        <header>
          <h1 className="text-xl font-semibold">{t("common.profile")}</h1>
          <p className="text-muted-foreground text-sm">
            {t("settings.profile_description")}
          </p>
        </header>
        <Card data-layout-frame="">
          <CardHeader>
            <CardTitle>{t("common.profile")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ProfileForm user={user} />
          </CardContent>
        </Card>
        <Card data-layout-frame="">
          <CardHeader>
            <CardTitle>{t("profile_ui.appearance")}</CardTitle>
          </CardHeader>
          <CardContent>
            <AppearanceForm />
          </CardContent>
        </Card>
        <Card data-layout-frame="">
          <CardHeader>
            <CardTitle>{t("profile_ui.sessions")}</CardTitle>
          </CardHeader>
          <CardContent className="divide-y">
            {sessions.map((session) => (
              <div
                key={session.id}
                className="flex flex-wrap items-center justify-between gap-4 py-3"
              >
                <div className="min-w-0 flex-1 basis-48 break-words">
                  <p className="text-sm font-medium">
                    {session.user_agent || t("profile_ui.unknown_browser")}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {session.ip_address}
                    {session.current
                      ? ` · ${t("profile_ui.current_session")}`
                      : ""}
                  </p>
                </div>
                {!session.current && (
                  <Form action={userSessionPath(session.id)} method="delete">
                    <Button type="submit" size="sm" variant="outline">
                      {t("profile_ui.revoke")}
                    </Button>
                  </Form>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </SettingsLayout>
  );
}
